import { useEffect, useMemo, useState } from 'react';
import type { Dashboard, TargetMap, Targets } from '../api';

// Suggested labels per dimension. Wealth/Sector are filled from the live
// dashboard so the editor reuses the buckets the user actually has, while
// Asset/Geo/Risk have a sensible fixed list (matches docs/screenshots).
const ASSET_LABELS = ['Equity', 'Bonds', 'Crypto', 'Cash', 'Property', 'Commodities', 'Other'];
const GEO_LABELS = ['United States', 'Europe', 'United Kingdom', 'Asia', 'Middle East', 'Other'];
const RISK_LABELS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10'];

type DimensionKey = 'wealth' | 'asset' | 'geo' | 'sector' | 'risk';

const DIMENSIONS: { key: DimensionKey; title: string; description: string }[] = [
  { key: 'wealth', title: 'Wealth', description: 'Split between household members.' },
  { key: 'asset', title: 'Asset class', description: 'Equity / Bonds / Cash / etc.' },
  { key: 'geo', title: 'Geography', description: 'Region the underlying companies sit in.' },
  { key: 'sector', title: 'Sector', description: 'GICS-style sector mix.' },
  {
    key: 'risk',
    title: 'Risk (1–10)',
    description: '1 = cash-like, 10 = single-country / crypto.',
  },
];

export function TargetsEditor({
  initial,
  dashboard,
  onSave,
  onCancel,
  saving,
}: {
  initial: Targets;
  dashboard: Dashboard | null;
  onSave: (next: Targets) => void;
  onCancel: () => void;
  saving: boolean;
}) {
  const [draft, setDraft] = useState<Targets>(() => cloneTargets(initial));

  useEffect(() => {
    setDraft(cloneTargets(initial));
  }, [initial]);

  const labelsByDim = useMemo(() => buildLabels(draft, dashboard), [draft, dashboard]);

  return (
    <div
      style={{
        border: '1px solid #ddd',
        borderRadius: 6,
        padding: '1rem',
        background: '#fafafa',
        marginTop: '1rem',
      }}
    >
      <h3 style={{ marginTop: 0 }}>Edit targets</h3>
      <p style={{ color: '#666', marginTop: 0 }}>
        Set the % of your portfolio you'd like in each bucket. Each dimension can sum to less or
        more than 100 — the page just shows what you typed.
      </p>
      {DIMENSIONS.map((d) => (
        <DimensionEditor
          key={d.key}
          title={d.title}
          description={d.description}
          values={draft[d.key]}
          labels={labelsByDim[d.key]}
          onChange={(next) => setDraft((prev) => ({ ...prev, [d.key]: next }))}
        />
      ))}
      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
        <button onClick={() => onSave(draft)} disabled={saving}>
          {saving ? 'Saving…' : 'Save targets'}
        </button>
        <button onClick={onCancel} disabled={saving} type="button">
          Cancel
        </button>
      </div>
    </div>
  );
}

function DimensionEditor({
  title,
  description,
  values,
  labels,
  onChange,
}: {
  title: string;
  description: string;
  values: TargetMap;
  labels: string[];
  onChange: (next: TargetMap) => void;
}) {
  const [newLabel, setNewLabel] = useState('');
  const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);

  function setValue(label: string, n: number) {
    const next = { ...values };
    if (Number.isNaN(n) || n <= 0) {
      delete next[label];
    } else {
      next[label] = round2(n);
    }
    onChange(next);
  }

  function addLabel() {
    const t = newLabel.trim();
    if (!t) return;
    if (values[t] != null) {
      setNewLabel('');
      return;
    }
    onChange({ ...values, [t]: 0 });
    setNewLabel('');
  }

  return (
    <section style={{ marginTop: '1rem' }}>
      <header style={{ display: 'flex', alignItems: 'baseline', gap: '0.75rem' }}>
        <strong>{title}</strong>
        <span style={{ color: '#666', fontSize: '0.85rem' }}>{description}</span>
        <span
          style={{
            marginLeft: 'auto',
            color: total === 100 ? '#10b981' : '#666',
            fontSize: '0.85rem',
          }}
        >
          sum {total.toFixed(2)}%
        </span>
      </header>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(120px, 1fr) 90px',
          rowGap: '0.3rem',
          columnGap: '0.5rem',
          marginTop: '0.4rem',
        }}
      >
        {labels.map((label) => (
          <Row
            key={label}
            label={label}
            value={values[label] ?? 0}
            onChange={(n) => setValue(label, n)}
          />
        ))}
      </div>
      <div style={{ marginTop: '0.4rem', display: 'flex', gap: '0.4rem' }}>
        <input
          type="text"
          placeholder="Add bucket"
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addLabel();
            }
          }}
          style={{ flex: 1, padding: '0.25rem 0.5rem', fontSize: '0.85rem' }}
        />
        <button type="button" onClick={addLabel}>
          Add
        </button>
      </div>
    </section>
  );
}

function Row({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (n: number) => void;
}) {
  return (
    <>
      <span style={{ alignSelf: 'center' }}>{label}</span>
      <input
        type="number"
        min={0}
        max={100}
        step={0.5}
        value={value === 0 ? '' : value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        style={{ padding: '0.25rem 0.4rem', fontSize: '0.85rem', textAlign: 'right' }}
      />
    </>
  );
}

function buildLabels(draft: Targets, dashboard: Dashboard | null): Record<DimensionKey, string[]> {
  return {
    wealth: union(Object.keys(draft.wealth), dashboard?.wealth.buckets.map((b) => b.label) ?? []),
    asset: union(Object.keys(draft.asset), ASSET_LABELS),
    geo: union(
      Object.keys(draft.geo),
      GEO_LABELS,
      dashboard?.geo.buckets.map((b) => b.label).filter((l) => l !== 'Unknown') ?? []
    ),
    sector: union(
      Object.keys(draft.sector),
      dashboard?.sector.buckets.map((b) => b.label).filter((l) => l !== 'Unknown') ?? []
    ),
    risk: union(Object.keys(draft.risk), RISK_LABELS),
  };
}

function union(...lists: string[][]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const list of lists) {
    for (const item of list) {
      if (!seen.has(item)) {
        seen.add(item);
        out.push(item);
      }
    }
  }
  return out;
}

function cloneTargets(t: Targets): Targets {
  return {
    wealth: { ...t.wealth },
    asset: { ...t.asset },
    geo: { ...t.geo },
    sector: { ...t.sector },
    risk: { ...t.risk },
  };
}

function round2(v: number): number {
  return Math.round(v * 100) / 100;
}
